import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import {
    clearUserValueFromStorage,
    loadUserValueFromStorage,
    saveUserValueToStorage,
} from 'custom_hooks/useStorage';
import { IssueMode } from 'pages/Desk/issues/shared';
import { DefaultDeskStateValues } from 'pages/Desk/issues/state/initializer';
import {
    DeskState,
    DeskStateValues,
    StoredDeskStateValues,
} from 'pages/Desk/issues/state/types';
import { ISSUE_FILTER_VALIDATION_CONSTRAINTS } from 'shared/issue_filters_context/StoreUtils';
import { getPartialObject, getPropertyNames } from 'utils/object';
import {
    isArray,
    isBoolean,
    isObjectValid,
    isPartialObjectValid,
    isString,
    ObjectConstraints,
    valueInList,
} from 'utils/object-validation';
import { OrderByDirection } from 'utils/query_v3';

const BaseDeskStorageKey = 'desk.state.storage';

const getDeskStorageKey = (tab: string, mode: IssueMode) =>
    `${tab}.${mode}.${BaseDeskStorageKey}`;

const ValidSortDirections: OrderByDirection[] = ['asc', 'desc'];
const isValidSortDirection = (value: unknown) => valueInList(value, ValidSortDirections);

const DataSourceSortConstraints: ObjectConstraints<DataSourceSort> = {
    field: isString,
    dir: isValidSortDirection,
};

const isValidDataSourceSort = (value: unknown): value is DataSourceSort =>
    isString(value) || isObjectValid(value, DataSourceSortConstraints);

const isValidSort = (value: unknown) => isArray(value, isValidDataSourceSort);

const DeskStateValueConstraints: ObjectConstraints<DeskStateValues> = {
    isInitialized: isBoolean,
    ...ISSUE_FILTER_VALIDATION_CONSTRAINTS,
    sort: isValidSort,
    queuedIssueIds: (value) => isArray(value, isString),
};

export const getDeskStateValues = (state: DeskState): DeskStateValues =>
    getPartialObject(state, getPropertyNames(DeskStateValueConstraints));

const StoredDeskStateValueConstraints: ObjectConstraints<StoredDeskStateValues> = {
    ...DeskStateValueConstraints,
    locationId: (value) => value === undefined || isString(value),
};

export const isValidDeskState = (
    state: unknown
): state is Partial<StoredDeskStateValues> =>
    isPartialObjectValid(state, StoredDeskStateValueConstraints);

const parseDeskState = (json: unknown): StoredDeskStateValues | null => {
    if (typeof json !== 'string') {
        return null;
    }
    try {
        const parsedState = JSON.parse(json);

        return isValidDeskState(parsedState)
            ? { ...DefaultDeskStateValues, ...parsedState }
            : null;
    } catch {
        return null;
    }
};

export const loadDeskStateFromBrowserStorage = (
    userEmail: string,
    tab: string,
    mode: IssueMode
) =>
    loadUserValueFromStorage<StoredDeskStateValues | null, unknown>(
        userEmail,
        getDeskStorageKey(tab, mode),
        null,
        {
            store: 'session',
            deserialize: parseDeskState,
        }
    ) ??
    loadUserValueFromStorage<StoredDeskStateValues | null, unknown>(
        userEmail,
        getDeskStorageKey(tab, mode),
        null,
        { store: 'local', deserialize: parseDeskState }
    ) ??
    undefined;

export const saveDeskStateToBrowserStorage = (
    userEmail: string,
    state: StoredDeskStateValues,
    tab: string,
    mode: IssueMode
) => {
    const stateValues = getPartialObject(
        state,
        getPropertyNames(StoredDeskStateValueConstraints)
    );
    const json = JSON.stringify(stateValues, null);
    const key = getDeskStorageKey(tab, mode);

    saveUserValueToStorage(userEmail, key, json, { store: 'session' });
    saveUserValueToStorage(userEmail, key, json, { store: 'local' });
};

export const clearDeskStateBrowserStorage = (
    userEmail: string,
    tab: string,
    mode: IssueMode
) => {
    const key = getDeskStorageKey(tab, mode);

    clearUserValueFromStorage(userEmail, key, 'session');
    clearUserValueFromStorage(userEmail, key, 'local');
};
